// Location pricing service for RV park calculator
import { supabase } from '../lib/supabase';
import locationsData from '../data/locations.json';

export const locationService = {
  // Enhanced search function for better location matching
  async getLocationPricing(location) {
    if (!location?.trim()) {
      return { data: null, error: 'Location is required' };
    }

    const searchTerm = this.extractSearchTermFromFormatted(location).toLowerCase();

    // 1. Try Supabase first (if available)
    try {
      if (supabase) {
        const { data, error } = await supabase.rpc('get_location_pricing', {
          search_location: searchTerm
        });

        if (!error && data && data.length > 0) {
          return { data, error: null };
        }
      }
    } catch (err) {
      // Silent fail to local
    }

    // 2. Fallback to local JSON data
    try {
      // Filter logic mimicking the SQL ILIKE search
      const data = locationsData.filter(item => {
        const city = item.city.toLowerCase();
        const state = item.state.toLowerCase();
        const region = item.region.toLowerCase();
        const zip = item.zip_code;
        
        return city.includes(searchTerm) || 
               state.includes(searchTerm) || 
               region.includes(searchTerm) || 
               zip.includes(searchTerm);
      }).sort((a, b) => {
        // Simple sorting logic: Exact city match first
        if (a.city.toLowerCase() === searchTerm) return -1;
        if (b.city.toLowerCase() === searchTerm) return 1;
        return 0;
      }).slice(0, 10); // Limit to 10 results

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Location search error:', error);
      return { data: null, error: 'Failed to fetch location pricing data' };
    }
  },

  // Extract clean search terms from formatted location strings
  extractSearchTermFromFormatted(formattedLocation) {
    if (!formattedLocation) return '';
    
    // Handle formatted strings like "Virginia Beach, VA (23451) • Mid-Atlantic • Pop: 459K"
    // Extract city and state for better database matching
    const parts = formattedLocation?.split('•')?.[0]?.trim(); // Get part before first •
    const zipMatch = parts?.match(/\((\d{5})\)/); // Extract zip code
    const cityStateMatch = parts?.replace(/\s*\(\d{5}\)\s*$/, '')?.trim(); // Remove zip code portion
    
    // Return the most specific available identifier
    if (zipMatch?.[1]) {
      return zipMatch?.[1]; // Use zip code for exact matching
    }
    
    if (cityStateMatch) {
      return cityStateMatch; // Use "City, State" format
    }
    
    // Fallback to original location if no parsing worked
    return formattedLocation;
  },

  // Get all available locations for suggestions with comprehensive data
  async getAllLocations() {
    try {
      // Use local JSON data instead of Supabase
      const data = locationsData.sort((a, b) => 
        (b.population_est || 0) - (a.population_est || 0)
      );

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get all locations error:', error);
      return { data: null, error: 'Failed to fetch location data' };
    }
  },

  // Format location for display with enhanced information
  formatLocationDisplay(locationData) {
    if (!locationData) return '';
    
    const { city, state, region, zip_code, population_est } = locationData;
    let display = `${city}, ${state}`;
    
    if (zip_code) display += ` (${zip_code})`;
    if (region) display += ` • ${region}`;
    if (population_est) {
      const popText = population_est >= 1000000 ? 
        `${(population_est / 1000000)?.toFixed(1)}M` : 
        `${(population_est / 1000)?.toFixed(0)}K`;
      
      display += ` • Pop: ${popText}`;
    }
    
    return display;
  },

  // Format number for display
  formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  },

  // Get pricing summary from location data
  getPricingSummary(pricingData) {
    if (!pricingData || pricingData.length === 0) {
      return {
        hasData: false,
        message: 'No pricing data available for this location.',
        recommendations: 'Try selecting a nearby city from the suggestions, or enter a zip code for more accurate results.'
      };
    }

    const firstLocation = pricingData[0];
    const averageRate = firstLocation?.suggested_nightly_short_usd || 65;
    const peakRate = Math.round(averageRate * 1.25);
    const offSeasonRate = Math.round(averageRate * 0.75);
    const monthlyRate = Math.round(averageRate * 25); // ~25 days at nightly rate
    const occupancyRate = firstLocation?.market_demand === 'high' ? 75 : 
                          firstLocation?.market_demand === 'medium' ? 65 : 55;

    return {
      hasData: true,
      averageRate,
      peakRate,
      offSeasonRate,
      monthlyRate,
      occupancyRate,
      peakMonths: 'Jun-Aug',
      seasonalData: {
        jan: 55, feb: 58, mar: 65, apr: 72, may: 80,
        jun: 90, jul: 95, aug: 92, sep: 78, oct: 68,
        nov: 58, dec: 52
      },
      message: `Market pricing data for ${firstLocation?.city}, ${firstLocation?.state}`,
      recommendations: `Based on ${firstLocation?.market_demand || 'medium'} demand and ${firstLocation?.competition_level || 'medium'} competition, we recommend starting with rates around $${averageRate}/night.`
    };
  },

  // Get market demographics from location data
  getMarketDemographics(locationData) {
    if (!locationData) return null;

    return {
      population: locationData.population_est ? {
        value: locationData.population_est,
        formatted: this.formatNumber(locationData.population_est)
      } : null,
      medianIncome: {
        value: 55000,
        formatted: '$55K'
      },
      medianHomePrice: {
        value: 300000,
        formatted: '$300K'
      },
      confidence: locationData.confidence || 0.7
    };
  },

  // Get seasonal insights from seasonal data
  getSeasonalInsights(seasonalData) {
    if (!seasonalData) return null;

    const months = Object.entries(seasonalData);
    const maxMonth = months.reduce((a, b) => a[1] > b[1] ? a : b);
    const minMonth = months.reduce((a, b) => a[1] < b[1] ? a : b);
    const variation = maxMonth[1] - minMonth[1];

    const monthNames = {
      jan: 'January', feb: 'February', mar: 'March', apr: 'April',
      may: 'May', jun: 'June', jul: 'July', aug: 'August',
      sep: 'September', oct: 'October', nov: 'November', dec: 'December'
    };

    const peakMonths = months
      .filter(([_, val]) => val >= maxMonth[1] - 10)
      .map(([key]) => monthNames[key] || key);

    return {
      peak: {
        months: peakMonths,
        value: maxMonth[1]
      },
      low: {
        month: monthNames[minMonth[0]] || minMonth[0],
        value: minMonth[1]
      },
      variation
    };
  }
};
